import React from 'react';
import clsx from 'clsx';
import { motion } from 'motion/react';

import {
  clampPanelWidth,
  type PanelWidthBounds
} from '~/components/workspaces/lib/artifact-panel-width';

const EASE_QUINT = [0.22, 1, 0.36, 1] as const;
const SLIDE = { duration: 0.28, ease: EASE_QUINT };
const INSTANT = { duration: 0 };

const KEY_STEP = 16;
const KEY_STEP_LARGE = 64;

/**
 * While a drag is underway the whole document shows the resize cursor and
 * selects nothing, since the pointer spends most of the drag off the handle.
 */
const useDocumentResizeCursor = (active: boolean) => {
  React.useEffect(() => {
    if (!active) return;
    const { cursor, userSelect } = document.body.style;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = cursor;
      document.body.style.userSelect = userSelect;
    };
  }, [active]);
};

/**
 * The artifact viewer's column at the right of the workspace row, resizable
 * from its left edge.
 *
 * Opening widens the column from nothing while the content inside holds the
 * final width and slides in, so the content is laid out once and revealed
 * rather than reflowed through every intermediate width. Closing eases back
 * the same way.
 *
 * Every other width change -- a drag, a key, a reset, the window resizing --
 * applies at once to both widths. Easing toward a target that moves on every
 * pointer or resize event leaves the edge trailing behind it. Motion applies
 * even an instant change on its next frame, so the column's width is also
 * written straight to the element, landing in the same paint as the content's.
 *
 * A drag re-renders this component per move but not the viewer: `children`
 * is the same element until the page itself re-renders.
 */
export const ArtifactPanel = (props: {
  width: number;
  bounds: PanelWidthBounds;
  onResize: (width: number) => void;
  onResizeEnd: (width: number) => void;
  onResetWidth: () => void;
  children: React.ReactNode;
}) => {
  const [dragWidth, setDragWidth] = React.useState<number | null>(null);
  const drag = React.useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    width: number;
  } | null>(null);
  const dragging = dragWidth !== null;
  const [opened, setOpened] = React.useState(false);
  const width = dragWidth ?? props.width;
  useDocumentResizeCursor(dragging);

  const asideRef = React.useRef<HTMLElement>(null);
  React.useLayoutEffect(() => {
    if ((opened || dragging) && asideRef.current) {
      asideRef.current.style.width = `${width}px`;
    }
  }, [opened, dragging, width]);

  const clamp = (next: number) => clampPanelWidth(next, props.bounds);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: props.width,
      width: props.width
    };
    setDragWidth(props.width);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    // The handle sits on the panel's left edge, so moving left widens it.
    const next = clamp(current.startWidth + current.startX - event.clientX);
    current.width = next;
    setDragWidth(next);
    props.onResize(next);
  };

  // A cancelled pointer reports no usable position, so the drag ends where it last moved to.
  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    drag.current = null;
    props.onResizeEnd(current.width);
    setDragWidth(null);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
    const next = {
      ArrowLeft: props.width + step,
      ArrowRight: props.width - step,
      Home: props.bounds.min,
      End: props.bounds.max
    }[event.key];
    if (next === undefined || !Number.isFinite(next)) return;
    event.preventDefault();
    props.onResizeEnd(clamp(next));
  };

  return (
    <motion.aside
      ref={asideRef}
      aria-label="Artifact viewer"
      initial={{ width: 0, opacity: 0 }}
      animate={{ width, opacity: 1 }}
      exit={{ width: 0, opacity: 0, transition: SLIDE }}
      transition={opened || dragging ? INSTANT : SLIDE}
      onAnimationComplete={() => setOpened(true)}
      className="relative hidden lg:flex flex-col flex-shrink-0 overflow-hidden bg-white dark:bg-neutral-800 border-l border-neutral-200 dark:border-neutral-700"
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize artifact viewer"
        aria-valuenow={width}
        aria-valuemin={props.bounds.min}
        aria-valuemax={Number.isFinite(props.bounds.max) ? props.bounds.max : undefined}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={props.onResetWidth}
        onKeyDown={onKeyDown}
        className="group absolute inset-y-0 left-0 z-10 w-2 cursor-col-resize touch-none focus:outline-none"
      >
        <div
          className={clsx(
            'h-full w-0.5 transition-colors duration-150',
            'group-hover:bg-primary-500 group-focus-visible:bg-primary-500',
            dragging && 'bg-primary-500'
          )}
        />
      </div>
      <motion.div
        initial={{ x: 24, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 24, opacity: 0 }}
        transition={SLIDE}
        style={{ width }}
        // An iframe in the viewer would otherwise take the pointer from the drag.
        className={clsx('h-full p-8 overflow-hidden', dragging && 'pointer-events-none')}
      >
        {props.children}
      </motion.div>
    </motion.aside>
  );
};
